import { Alert, Box, Tbody, Td, Th, Thead, Tr, Table } from '@ff-it/ui';
import useSWR, { SWRResponse } from 'swr';
import { ActivityType } from 'types';
import { EmbeddedProduct, ProductName } from '../products';
import { Outlet } from '../outletOrServiceCategory/types';
import { useMemo } from 'react';
import { DecimalField } from '@ff-it/form';
import { OutletOrServiceCategoryTitle } from '../outletOrServiceCategory/OutletOrServiceCategoryTitle';

type PricesFormProps = { type: ActivityType; vendorId: number };

export function PricesForm({ type, vendorId }: PricesFormProps) {
  const { data: outlets } = useSWR<Outlet[], any, { suspense: true }>(
    {
      url: `supplier/outlet_or_service_category/`,
      method: 'GET',
      queryParams: { vendor: vendorId },
    },
    { suspense: true },
  );

  const { data: products }: SWRResponse<EmbeddedProduct[], any, { suspense: true }> = useSWR(
    {
      url: `supplier/products/`,
      method: 'GET',
      queryParams: { type, outlet_or_service_category__vendor: vendorId },
    },
    { suspense: true },
  );

  const productsByOutlet = useMemo(() => {
    const res: Record<number, EmbeddedProduct[]> = {};
    for (const outlet of outlets) {
      res[outlet.id] = [];
    }
    for (const product of products || []) {
      const outletId = product.outlet_or_service_category.id;
      if (outletId in res) {
        res[outletId].push(product);
      }
    }
    return res;
  }, [products, outlets]);

  const nonEmptyOutlets = outlets.filter((outlet) => productsByOutlet[outlet.id]?.length > 0);

  if (nonEmptyOutlets.length === 0) {
    return <Alert variant="info">No products matching.</Alert>;
  }

  return (
    <Box style={{ maxWidth: '32rem' }}>
      {nonEmptyOutlets.map((outlet) => (
        <Table mb="3" size="xs" key={outlet.id} bordered fixed>
          <Thead>
            <Tr>
              <Th>
                <OutletOrServiceCategoryTitle {...outlet} />
              </Th>
              <Th style={{ width: '120px' }}></Th>
              <Th style={{ width: '120px' }}>Gross price</Th>
            </Tr>
          </Thead>
          <Tbody>
            {productsByOutlet[outlet.id].map((product) => (
              <Tr key={product.id}>
                <Td>
                  <ProductName name={product.name} asTitle />
                </Td>
                <Td>{product.kind}</Td>
                <Td>
                  <DecimalField precision={6} name={`prices._${product.id}`} size="sm" className="mb-0 text-right" />
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      ))}
    </Box>
  );
}
